import { memo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import EastIcon from '@mui/icons-material/East';
import { parse } from "util/parsers";
import { when } from "util/support";
import JsonDiff from "lib/json-diff";
import Tabs, { Tab } from "ui/atoms/Tabs";
import { useChange, useColumns } from "../../support";
import type { ColumnsProps } from "../State";
import type { DiffColumn } from "../../repository";

const Jsonable = (props: ColumnsProps) => {
    const { withAvailableColumns, getObjectColumns } = useColumns();
    const { getChangedDiffs } = useChange();

    const getModel = () => {
        const tabs: Array<Tab> = [];

        for (const [key, { previous, current }] of Object.entries(getChangedDiffs({ row: props.row }))) {
            const [colDef] = withAvailableColumns(getObjectColumns, [key as DiffColumn]);

            if (colDef) {
                const prevJsonable = parse(previous, {});

                tabs.push({
                    label: (
                        <>
                            {colDef.headerName}
                        </>
                    ),
                    component: (
                        <Box
                            sx={{
                                height: 'calc(100vh - 380px)',
                                overflow: 'auto',

                                '& .json-diff-viewer': {
                                    '& .line-equal:not(.line-number)': {
                                        minWidth: 'calc(50vw - 100px)'
                                    }
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'sticky',
                                    bgcolor: 'background.paper',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1,
                                    pb: 2
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 18,
                                        fontWeight: 'bold'
                                    }}
                                >
                                    Previous
                                </Typography>
                                <EastIcon />
                                <Typography
                                    sx={{
                                        fontSize: 18,
                                        fontWeight: 'bold'
                                    }}
                                >
                                    Current
                                </Typography>
                            </Box>
                            <JsonDiff
                                sources={[
                                    when(
                                        Array.isArray(prevJsonable),
                                        {},
                                        prevJsonable
                                    ),
                                    parse(current, {})
                                ]}
                                differOptions={{
                                    detectCircular: true,
                                    maxDepth: 0,
                                    showModifications: true,
                                    arrayDiffMethod: 'lcs',
                                    recursiveEqual: true
                                }}
                                viewerProps={{
                                    indent: 4,
                                    lineNumbers: true,
                                    highlightInlineDiff: true,
                                    inlineDiffOptions: {
                                        mode: 'word'
                                    },
                                    syntaxHighlight: {
                                        theme: 'monokai'
                                    },
                                }}
                            />
                        </Box>
                    )
                });
            }
        }

        return tabs;
    };

    return (
        <Tabs
            model={getModel()}
            TabListContainerProps={{
                borderBottom: 1,
                borderColor: 'divider'
            }}
            TabListProps={{
                'aria-label': 'jsonable',
                variant: 'scrollable',
                scrollButtons: 'auto'
            }}
        />
    );
};

export default memo(Jsonable);
